// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { TextField } from '@mui/material';
import { Heading, type TitleVariant } from '@shared/ui-kit/components';
import { IconButton } from '@shared/ui-kit/components/icon-button';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

export interface EditableTextProps {
  text: string | null;
  onEdit: (text: string) => void;
  className?: string;
  placeholder?: string;
  headingVariant?: TitleVariant;
}

export const EditableText: React.FC<EditableTextProps> = ({
  text,
  onEdit,
  className = '',
  placeholder = '',
  headingVariant = 'h3',
}) => {
  const [editing, setEditing] = useState(false);
  const [editedText, setEditedText] = useState(text);
  const [showEditButton, setShowEditButton] = useState(false);

  const saveChanges = useCallback(() => {
    setEditing(false);
    onEdit(editedText || '');
  }, [editedText, onEdit]);

  const handleEditClick = useCallback(() => {
    setEditing(true);
  }, []);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEditedText(event.target.value);
    },
    [],
  );

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      /* istanbul ignore next */
      if (event.key === 'Enter') {
        saveChanges();
      }
    },
    [saveChanges],
  );

  const handleBlur = useCallback(() => {
    saveChanges();
  }, [saveChanges]);

  const handleMouseEnter = useCallback(() => {
    setShowEditButton(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setShowEditButton(false);
  }, []);

  const isEditButtonVisible = useMemo(
    () => showEditButton || editing,
    [editing, showEditButton],
  );

  useEffect(() => {
    setEditedText(text);
  }, [text]);

  return (
    <div
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid="editable-text-container"
    >
      {editing ? (
        <TextField
          variant="standard"
          InputProps={{
            classes: {
              root: 'input-edit-saved-portfolio',
            },
          }}
          value={editedText}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          onBlur={handleBlur}
          autoFocus
          placeholder={placeholder}
        />
      ) : (
        <Heading title={editedText || placeholder} variant={headingVariant} />
      )}
      {isEditButtonVisible && (
        <IconButton
          className="ml-3"
          icon="Pen"
          variant="icon"
          onClick={handleEditClick}
          size="lg"
          data-testid="edit-text-btn"
        />
      )}
    </div>
  );
};
